/**
 * 乘车过场的场景生成
 *
 * 为什么是「程序化矢量」而不是美术图：
 * 线路现在有 200+ 个节点，逐站配图意味着每加一个地点都要出图、传 OSS、再同步首尔静态；
 * 而且游戏现有街景素材（/oss/streetbg.png）本身就是纯色块风，矢量程序化与它同一套语言。
 *
 * 分层约定（与 coachPassage.vue 的 CSS 严格对应，改这里必须同步改那边的层高度）：
 *   far  0% ~ 74%   天空 + 日/月 + 云 + 远山 + 天际线 + 地平线地面带
 *   mid  34% ~ 68%  建筑/树木/设施，底部 68% 左右是地平线
 *   near 70% ~ 100% 草地/路面/车道线，滚动最快
 * far 的内容整体 scale(1, 0.865) 压回原位：层从 64% 拉到 74% 是为了消除黑缝，
 * 不压的话山和天际线会整体下移，和 mid 的建筑对不上。
 */

/* ---------- SVG 小工具 ---------- */
const rect = (x, y, w, h, fill, rx = 0, extra = '') =>
  `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${fill}"${rx ? ` rx="${rx}"` : ''}${extra}/>`
const circ = (cx, cy, r, fill, extra = '') =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}"${extra}/>`
const path = (d, fill, extra = '') => `<path d="${d}" fill="${fill}"${extra}/>`
const toURI = svg => `url("data:image/svg+xml;charset=utf8,${encodeURIComponent(svg)}")`

/* ---------- 树 / 建筑元件 ---------- */
function roundTree(x, base, s, leaf = '#6fae57', leaf2 = '#87c46b', trunk = '#7b5b3f') {
  return path(`M${x - 4} ${base} L${x + 4} ${base} L${x + 3} ${base - 26 * s} L${x - 3} ${base - 26 * s} Z`, trunk)
    + circ(x, base - 34 * s, 17 * s, leaf)
    + circ(x - 9 * s, base - 28 * s, 12 * s, leaf2)
    + circ(x + 10 * s, base - 30 * s, 11 * s, leaf2)
}
function pineTree(x, base, s, c1 = '#3f7d4e', c2 = '#4f9259', trunk = '#5d4632') {
  return rect(x - 3 * s, base - 16 * s, 6 * s, 16 * s, trunk)
    + path(`M${x} ${base - 96 * s} L${x + 26 * s} ${base - 42 * s} L${x - 26 * s} ${base - 42 * s} Z`, c1)
    + path(`M${x} ${base - 70 * s} L${x + 30 * s} ${base - 16 * s} L${x - 30 * s} ${base - 16 * s} Z`, c2)
}
function poplar(x, base, s, c = '#7fae5c', c2 = '#96c072') {
  return rect(x - 3, base - 20 * s, 6, 20 * s, '#6b533c')
    + path(`M${x} ${base - 150 * s} C${x + 16 * s} ${base - 120 * s} ${x + 15 * s} ${base - 40 * s} ${x} ${base - 20 * s} C${x - 15 * s} ${base - 40 * s} ${x - 16 * s} ${base - 120 * s} ${x} ${base - 150 * s} Z`, c)
    + path(`M${x} ${base - 130 * s} C${x + 8 * s} ${base - 105 * s} ${x + 8 * s} ${base - 50 * s} ${x} ${base - 34 * s} C${x - 8 * s} ${base - 50 * s} ${x - 8 * s} ${base - 105 * s} ${x} ${base - 130 * s} Z`, c2)
}
function lamp(x, base, h = 120, pole = '#5b6470', glow = '#ffe9a8') {
  return rect(x - 3, base - h, 6, h, pole)
    + path(`M${x - 3} ${base - h} q0 -14 14 -14 l10 0 l0 6 l-10 0 q-8 0 -8 8 Z`, pole)
    + circ(x + 14, base - h - 11, 7, glow, ' opacity="0.9"')
    + circ(x + 14, base - h - 11, 13, glow, ' opacity="0.18"')
}
function windows(x, y, cols, rows, w, h, gapx, gapy, fill) {
  let s = ''
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    s += rect(x + c * (w + gapx), y + r * (h + gapy), w, h, fill, 1.5)
  }
  return s
}
// 亮灯窗：按固定规律灭几盏，避免整面墙一样亮
function winGrid(x, y, cols, rows, w, h, gx, gy, c, op = 1) {
  let s = ''
  for (let i = 0; i < rows; i++) for (let j = 0; j < cols; j++) {
    if ((i * 5 + j * 3) % 7 === 0) continue
    s += rect(x + j * (w + gx), y + i * (h + gy), w, h, c, 1, ` opacity="${op}"`)
  }
  return s
}

/* ---------- 各主题中景（206 高，基线 y=252） ---------- */
function midPlaza() {
  let s = ''
  // 石柱廊：柱身 + 柱头柱础 + 连续檐口
  const N = 7, x0 = 40, gap = 88
  for (let i = 0; i < N; i++) {
    const x = x0 + i * gap
    s += rect(x - 11, 112, 22, 120, '#c7c0a9', 4)
    s += rect(x - 6, 122, 4, 102, 'rgba(0,0,0,.08)')
    s += rect(x - 15, 104, 30, 10, '#d5cdb6', 3)
    s += rect(x - 16, 226, 32, 12, '#b4ac96', 3)
  }
  s += rect(x0 - 32, 88, gap * (N - 1) + 64, 18, '#cec6af', 4)
  s += rect(x0 - 42, 238, gap * (N - 1) + 84, 12, '#bdb49c', 3)
  // 喷泉：三层水盘 + 水柱
  s += rect(690, 232, 260, 20, '#c2bba4', 6)
  s += rect(702, 222, 236, 12, '#b3d3d6', 5)
  s += rect(808, 194, 28, 32, '#cdc5ae', 4)
  s += rect(766, 166, 112, 14, '#d8d0b8', 5)
  s += rect(778, 158, 88, 10, '#b3d3d6', 4)
  s += rect(815, 132, 14, 28, '#cdc5ae', 4)
  s += rect(798, 126, 48, 10, '#d8d0b8', 4)
  s += rect(819, 100, 6, 28, 'rgba(210,240,245,.9)', 3)
  s += '<path d="M822 104 q-34 -22 -56 4" stroke="rgba(210,240,245,.75)" stroke-width="4" fill="none" stroke-linecap="round"/>'
  s += '<path d="M822 104 q34 -22 56 4" stroke="rgba(210,240,245,.75)" stroke-width="4" fill="none" stroke-linecap="round"/>'
  // 纪念雕像
  s += rect(1196, 226, 124, 26, '#c2bba4', 3)
  s += rect(1214, 208, 88, 20, '#cec6af', 3)
  s += circ(1258, 172, 17, '#bcb49c')
  s += path('M1258 188 q-28 9 -32 40 l64 0 q-4 -31 -32 -40 Z', '#bcb49c')
  s += path('M1234 198 l-20 -32 l10 -7 l20 32 Z', '#bcb49c')
  // 绿化与路灯
  s += roundTree(620, 250, 1, '#79b25e', '#8fc76e')
  s += roundTree(1290, 250, 0.9, '#79b25e', '#8fc76e')
  s += roundTree(1460, 250, 1.1, '#79b25e', '#8fc76e')
  s += lamp(196, 250, 130, '#4f5a66', '#ffeeb2')
  s += lamp(1120, 250, 130, '#4f5a66', '#ffeeb2')
  return s
}
function midAvenue() {
  let s = ''
  const bs = [
    { x: 40, w: 210, h: 250, c: '#8fa0ad', c2: '#a3b4c0' },
    { x: 280, w: 170, h: 222, c: '#7d8e9c', c2: '#93a5b2' },
    { x: 480, w: 230, h: 200, c: '#98a8b4', c2: '#adbdc8' },
    { x: 960, w: 190, h: 214, c: '#82939f', c2: '#98aab6' },
    { x: 1180, w: 200, h: 190, c: '#8fa0ad', c2: '#a3b4c0' },
    { x: 1400, w: 180, h: 226, c: '#7c8d9a', c2: '#92a4b1' }
  ]
  bs.forEach(o => {
    s += rect(o.x, 250 - o.h, o.w, o.h, o.c)
    s += rect(o.x, 250 - o.h, o.w, 10, o.c2)
    s += windows(o.x + 16, 250 - o.h + 26, Math.floor((o.w - 32) / 26), Math.floor((o.h - 46) / 34), 14, 18, 12, 16, 'rgba(255,255,255,.34)')
  })
  s += roundTree(760, 252, 1, '#6fae57', '#87c46b')
  s += roundTree(900, 252, 0.9, '#6fae57', '#87c46b')
  s += roundTree(660, 252, 0.75, '#6fae57', '#87c46b')
  s += lamp(200, 252, 138) + lamp(560, 252, 138) + lamp(1120, 252, 138) + lamp(1560, 252, 138)
  return s
}
function midResidence() {
  let s = ''
  const hs = [
    { x: 60, w: 190, h: 120, wall: '#efe3cd', roof: '#b5563f' },
    { x: 290, w: 160, h: 96, wall: '#e9dcc2', roof: '#9d4f3c' },
    { x: 700, w: 210, h: 130, wall: '#f2e7d2', roof: '#a9543d' },
    { x: 960, w: 170, h: 104, wall: '#e7d9be', roof: '#b5563f' },
    { x: 1220, w: 200, h: 122, wall: '#efe3cd', roof: '#9d4f3c' }
  ]
  hs.forEach(o => {
    s += rect(o.x, 252 - o.h, o.w, o.h, o.wall)
    s += path(`M${o.x - 16} ${252 - o.h} L${o.x + o.w / 2} ${252 - o.h - 62} L${o.x + o.w + 16} ${252 - o.h} Z`, o.roof)
    s += rect(o.x + o.w - 46, 252 - o.h - 52, 18, 34, o.roof)
    s += rect(o.x + 22, 252 - o.h + 24, 34, 30, '#7e9fb0', 2)
    s += rect(o.x + o.w - 66, 252 - o.h + 24, 34, 30, '#7e9fb0', 2)
    s += rect(o.x + o.w / 2 - 16, 252 - 58, 32, 58, '#8b6a4a', 2)
  })
  s += roundTree(560, 252, 0.95, '#78ad5c', '#8ec46f')
  s += roundTree(1180, 252, 0.8, '#78ad5c', '#8ec46f')
  s += lamp(260, 252, 118, '#5b5450', '#ffd9a0')
  s += lamp(910, 252, 118, '#5b5450', '#ffd9a0')
  return s
}
function midFarm() {
  let s = ''
  // 风车（注意：SVG 作为 background-image 时内部 CSS 动画不会播，叶片是静态的）
  s += path('M300 252 L314 132 L346 132 L360 252 Z', '#e8ddc6')
  s += path('M306 132 L354 132 L330 104 Z', '#b5563f')
  s += path('M330 132 L330 40 L338 44 L330 132 Z', '#8b6a4a')
  s += path('M330 132 L420 122 L418 132 L330 140 Z', '#8b6a4a')
  s += path('M330 132 L330 224 L322 220 L330 132 Z', '#8b6a4a')
  s += path('M330 132 L240 142 L242 132 L330 124 Z', '#8b6a4a')
  // 谷仓
  s += rect(700, 168, 190, 84, '#b5563f') + path('M686 168 L795 108 L904 168 Z', '#8f4232')
  s += rect(770, 200, 50, 52, '#e8ddc6', 2)
  s += rect(760, 246, 70, 6, '#8f4232')
  // 草垛
  s += circ(960, 238, 16, '#e3ce8c') + circ(992, 242, 12, '#d9c179')
  // 栅栏
  for (let i = 0; i < 14; i++) s += rect(1030 + i * 40, 214, 7, 38, '#cdbb96')
  s += rect(1024, 222, 566, 6, '#d8c8a6') + rect(1024, 236, 566, 6, '#d8c8a6')
  s += roundTree(140, 252, 1.05, '#79b25e', '#8fc76e')
  s += roundTree(600, 252, 0.8, '#79b25e', '#8fc76e')
  s += roundTree(1180, 252, 0.9, '#79b25e', '#8fc76e')
  return s
}
function midGate() {
  let s = ''
  // 城墙
  s += rect(0, 96, 640, 156, '#b6bdb4')
  s += rect(0, 88, 640, 14, '#c6ccc3')
  for (let i = 0; i < 16; i++) s += rect(6 + i * 40, 78, 24, 12, '#c6ccc3')
  // 塔楼（左）
  s += rect(60, 20, 100, 232, '#a9b2a8') + rect(48, 12, 124, 16, '#c0c7bd')
  for (let i = 0; i < 3; i++) s += rect(58 + i * 40, 0, 22, 14, '#c0c7bd')
  s += rect(92, 60, 36, 46, '#5d6a63', 3)
  // 城门
  s += path('M700 252 L700 130 Q700 62 790 62 Q880 62 880 130 L880 252 Z', '#5c6660')
  s += path('M718 252 L718 138 Q718 82 790 82 Q862 82 862 138 L862 252 Z', '#48524d')
  // 塔楼（右）
  s += rect(900, 24, 104, 228, '#a9b2a8') + rect(888, 16, 128, 16, '#c0c7bd')
  for (let i = 0; i < 3; i++) s += rect(898 + i * 40, 4, 22, 14, '#c0c7bd')
  // 旗帜
  s += rect(946, 0, 4, 76, '#6b736a') + path('M950 8 L1004 24 L950 40 Z', '#b5563f')
  s += rect(110, 0, 4, 76, '#6b736a') + path('M114 8 L168 24 L114 40 Z', '#b5563f')
  s += lamp(620, 252, 110, '#5b6470') + lamp(1010, 252, 110, '#5b6470')
  s += roundTree(1200, 252, 0.9, '#6fae57', '#87c46b') + roundTree(1420, 252, 1.05, '#6fae57', '#87c46b')
  return s
}
function midForest() {
  let s = ''
  const xs = [40, 130, 250, 380, 500, 620, 760, 880, 1010, 1140, 1270, 1400, 1520]
  xs.forEach((x, i) => { s += pineTree(x, 254, 0.85 + (i % 3) * 0.16) })
  s += poplar(330, 254, 1.15) + poplar(1060, 254, 1.05) + poplar(690, 254, 0.95)
  s += path('M470 254 q16 -26 40 -18 q20 6 26 18 Z', '#9aa89a')
  s += path('M1220 254 q14 -20 34 -14 q18 6 22 14 Z', '#9aa89a')
  return s
}
function midService() {
  let s = ''
  // 加油站顶棚 + 加油机
  s += rect(180, 96, 300, 20, '#e6e9ea', 3) + rect(180, 116, 300, 8, '#b5563f')
  s += rect(196, 124, 12, 128, '#cdd3d5') + rect(452, 124, 12, 128, '#cdd3d5')
  s += rect(260, 176, 46, 76, '#e2e5e6', 3) + rect(268, 184, 30, 26, '#7e9fb0', 2)
  s += rect(360, 176, 46, 76, '#e2e5e6', 3) + rect(368, 184, 30, 26, '#7e9fb0', 2)
  // 便利店
  s += rect(620, 140, 230, 112, '#f0eee7')
  s += path('M606 140 L735 96 L864 140 Z', '#b5563f')
  s += rect(644, 176, 60, 76, '#7e9fb0', 2) + rect(724, 176, 96, 76, '#94b6c6', 2)
  s += rect(700, 118, 74, 20, '#2f6f8f', 3)
  // 路牌
  s += rect(1230, 60, 8, 192, '#8f979e')
  s += rect(1166, 44, 150, 54, '#2f6f8f', 4)
  s += rect(1176, 74, 96, 8, 'rgba(255,255,255,.75)', 2)
  // 白杨一排
  s += poplar(980, 252, 1.1) + poplar(1050, 252, 0.95) + poplar(1130, 252, 1.05) + poplar(1400, 252, 1.0) + poplar(1470, 252, 1.15)
  s += lamp(560, 252, 126) + lamp(1300, 252, 126)
  return s
}
// 高速设施：龙门架 + 匝道护栏 + 路灯，用于「出口 / 匝道 / 立交 / 停车区」
function midHighway() {
  let s = ''
  const gantry = (x, w, pole, beam, sign) => {
    let g = rect(x, 40, 12, 212, pole) + rect(x + w - 12, 40, 12, 212, pole)
    g += rect(x - 6, 34, w + 12, 14, beam)
    g += rect(x + 44, 48, 118, 46, sign, 4)
    g += rect(x + 56, 62, 76, 8, 'rgba(255,255,255,.82)', 2)
    g += rect(x + 56, 76, 44, 8, 'rgba(255,255,255,.5)', 2)
    return g
  }
  s += gantry(130, 380, '#9aa5ab', '#a8b2b8', '#2f6f8f')
  s += gantry(760, 340, '#a6b0b5', '#b2bcc1', '#3d7a99')
  // 护栏
  for (let i = 0; i < 42; i++) s += rect(i * 40, 224, 6, 28, '#c2cad0')
  s += rect(0, 230, 1600, 5, '#ccd3d8')
  // 远处的平原树与电线杆
  s += roundTree(1180, 252, 0.75, '#7fb162', '#95c273')
  s += roundTree(1260, 252, 0.62, '#7fb162', '#95c273')
  s += roundTree(560, 252, 0.7, '#7fb162', '#95c273')
  s += rect(1460, 116, 6, 136, '#a6b0b5')
  s += rect(1440, 116, 46, 5, '#a6b0b5') + rect(1442, 128, 42, 4, '#b2bcc1')
  s += lamp(1040, 252, 132)
  s += lamp(220, 252, 132)
  return s
}
/* ---- 以下 8 个是「公路子类型」----
 * 维幻线 24 站里有 13 站属于 highway 大类，全画成同一条公路的话，
 * 一趟车坐下来会有大半时间是重复画面。所以按站点名再分：收费站 / 出口 / 观景台 /
 * 隧道 / 大桥 / 桥头堡 / 立交。它们共用 high 系配色，只换中景。 */

// 车站：站房 + 雨棚 + 月台（线路首尾两站）
function midStation() {
  let s = ''
  // 站房
  s += rect(560, 96, 470, 156, '#eee7d8')
  s += path('M536 96 L795 46 L1054 96 Z', '#a2553f')
  s += rect(560, 96, 470, 10, '#d8cfbc')
  // 钟楼
  s += rect(768, 30, 54, 70, '#e6dfd0')
  s += path('M756 34 L795 4 L834 34 Z', '#8f4a37')
  s += circ(795, 60, 20, '#f7f2e2') + circ(795, 60, 15, '#c9bfa8')
  s += rect(793, 48, 4, 13, '#5f574a') + rect(795, 58, 11, 3, '#5f574a')
  // 站房门窗
  s += rect(600, 140, 90, 112, '#7e9fb0', 2) + rect(710, 140, 90, 112, '#8fb0c0', 2)
  s += rect(880, 150, 100, 76, '#a8c4d2', 3)
  s += rect(880, 150, 100, 6, '#c9dbe5', 2)
  // 雨棚（月台）
  s += rect(60, 128, 470, 16, '#cfd8dc', 3)
  s += rect(60, 144, 470, 7, '#2f6f8f')
  for (let i = 0; i < 5; i++) s += rect(96 + i * 108, 144, 10, 108, '#b6c0c5')
  // 站台边缘 + 长椅
  s += rect(40, 236, 500, 16, '#d8dbd6')
  s += rect(40, 236, 500, 4, '#eef1ed')
  s += rect(320, 214, 66, 12, '#a2805c', 3) + rect(326, 226, 6, 12, '#8d6f4f') + rect(376, 226, 6, 12, '#8d6f4f')
  // 站牌
  s += rect(232, 186, 8, 50, '#7d878c') + rect(186, 172, 100, 26, '#2f6f8f', 3)
  s += rect(198, 180, 76, 9, 'rgba(255,255,255,.8)', 2)
  // 树与灯
  s += roundTree(1120, 252, 1.0) + roundTree(1210, 252, 0.82) + roundTree(1420, 252, 1.05)
  s += lamp(1090, 252, 130) + lamp(1330, 252, 130)
  return s
}

// 收费站：雨棚 + 收费亭 + 抬杆
function midToll() {
  let s = ''
  for (let i = 0; i < 7; i++) s += rect(126 + i * 210, 84, 16, 168, '#c4cbd0')
  s += rect(96, 56, 1408, 30, '#e3e8ea', 4)
  s += rect(96, 86, 1408, 9, '#2f6f8f')
  s += rect(100, 56, 1400, 6, '#f3f6f7', 3)
  s += rect(688, 18, 224, 30, '#2f6f8f', 4)
  s += rect(706, 27, 188, 11, 'rgba(255,255,255,.72)', 2)
  for (let i = 0; i < 7; i++) {
    const x = 208 + i * 210
    s += rect(x, 118, 64, 118, '#eff2f3', 4)
    s += rect(x + 9, 128, 46, 38, '#8fb3c4', 2)
    s += rect(x + 9, 176, 46, 30, '#c7d0d4', 2)
    // 抬杆：一半抬起一半放下，比整排一致更像实景
    if (i % 3 === 1) {
      s += rect(x + 66, 168, 6, 24, '#cfd5d8')
      s += rect(x + 70, 150, 74, 6, '#e0574a', 2, ' transform="rotate(-28 ' + (x + 70) + ' 153)"')
    } else {
      s += rect(x + 66, 192, 6, 30, '#cfd5d8')
      s += rect(x + 70, 194, 80, 6, '#e0574a', 2)
    }
  }
  s += roundTree(52, 252, 0.85) + roundTree(1532, 252, 0.9)
  s += lamp(470, 252, 126) + lamp(1150, 252, 126)
  return s
}

// 出口 / 匝道：分流道 + 绿色出口牌 + 分流鼻端

function midExit() {
  let s = ''
  // 桥墩必须先画：后画的话会压在匝道面上，看着像马路中间立了几根杆
  for (let i = 0; i < 6; i++) {
    const px = 1080 + i * 96
    const topY = 246 - i * 22
    s += rect(px, topY, 15, 252 - topY, '#8d979d')
    s += rect(px, topY, 5, 252 - topY, 'rgba(255,255,255,.18)')
  }
  // 高架匝道：向右后方抬起并跨过主路
  s += path('M1020 252 C1140 252 1200 190 1350 148 L1600 92 L1600 134 L1370 180 C1236 216 1160 252 1040 252 Z', '#8a9298')
  s += path('M1020 252 C1140 252 1200 190 1350 148 L1600 92', 'none', ' stroke="#dfe5e8" stroke-width="5"')
  s += path('M1020 252 C1140 252 1200 190 1350 148 L1600 92 L1600 108 L1352 162 C1206 204 1140 264 1032 264 Z', 'rgba(30,44,52,.14)')
  // 分流鼻端：防撞桶 + 导向箭头
  for (let i = 0; i < 4; i++) s += rect(1016 + i * 24, 224, 18, 28, i % 2 ? '#e0a13a' : '#c9ced2', 3)
  // 出口龙门架
  s += rect(206, 48, 14, 204, '#9aa5ab') + rect(700, 48, 14, 204, '#9aa5ab')
  s += rect(194, 40, 524, 16, '#a8b2b8', 3)
  s += rect(248, 62, 404, 86, '#2f7d4f', 5)
  s += rect(248, 62, 404, 7, '#3e9a63', 3)
  s += rect(272, 88, 152, 15, 'rgba(255,255,255,.92)', 3)
  s += rect(272, 114, 98, 13, 'rgba(255,255,255,.7)', 3)
  s += path('M582 86 L622 86 L622 72 L654 100 L622 128 L622 114 L582 114 Z', '#ffffff')
  // 黄黑警示柱
  s += rect(772, 194, 16, 58, '#e0a13a') + rect(772, 212, 16, 9, '#3a3f44')
  s += roundTree(880, 252, 0.7) + roundTree(1000, 252, 0.62)
  s += lamp(120, 252, 128)
  return s
}

// 观景台：山顶平台 + 木栏杆 + 望远镜 + 景区牌
function midViewpoint() {
  let s = ''
  // 近处山坡（viewBox 只有 260 高，坡顶压在 150 附近才不会顶出图层）
  s += path('M0 252 L0 168 C150 142 300 162 470 192 C620 218 760 208 924 190 L924 252 Z', '#9ec78f')
  s += path('M0 252 L0 208 C160 194 320 214 480 226 C620 236 780 224 924 214 L924 252 Z', '#8bb97d')
  // 平台
  s += rect(900, 198, 540, 54, '#c9c3b0', 4)
  s += rect(900, 198, 540, 9, '#dcd6c3', 3)
  // 木栏杆
  s += rect(898, 148, 546, 10, '#a3805b', 3)
  s += rect(898, 180, 546, 6, '#a3805b', 2)
  for (let i = 0; i < 14; i++) s += rect(906 + i * 39, 152, 8, 48, '#8a6a4a')
  // 望远镜
  s += rect(1012, 166, 8, 34, '#4f5a66')
  s += rect(988, 146, 44, 22, '#5d6b78', 7) + rect(1032, 150, 16, 8, '#46525c', 3)
  s += path('M988 150 l-22 10 l0 6 l22 6 Z', '#5d6b78')
  // 景区指示牌（棕色）
  s += rect(1268, 80, 9, 120, '#8a6a4a')
  s += rect(1184, 50, 178, 66, '#7a5a3c', 4)
  s += rect(1184, 50, 178, 6, '#8d6a48', 3)
  s += rect(1200, 68, 112, 11, 'rgba(255,255,255,.82)', 2)
  s += rect(1200, 88, 76, 10, 'rgba(255,255,255,.55)', 2)
  // 长椅 + 垃圾桶
  s += rect(1120, 216, 70, 13, '#a2805c', 3) + rect(1126, 229, 6, 14, '#8d6f4f') + rect(1180, 229, 6, 14, '#8d6f4f')
  s += rect(1400, 214, 22, 38, '#6f7a72', 3)
  // 左侧山石与松林（否则整格左边是一大片空草地）
  s += path('M120 252 L120 214 Q170 196 226 210 L250 252 Z', '#a8b5a0')
  s += path('M238 252 L238 226 Q290 210 340 226 L360 252 Z', '#9caa95')
  s += pineTree(60, 252, 0.85) + pineTree(400, 252, 1.0) + pineTree(480, 252, 0.72)
  s += pineTree(560, 252, 0.88) + pineTree(690, 252, 0.66)
  return s
}

// 隧道：山体 + 拱形洞口 + 洞内纵深灯
function midTunnel() {
  let s = ''
  // 山体两层
  s += path('M0 252 L0 148 C130 104 280 92 430 106 C580 120 690 90 800 76 C940 58 1080 92 1220 100 C1380 110 1490 92 1600 112 L1600 252 Z', '#93ab8c')
  s += path('M0 252 L0 194 C170 170 330 188 480 202 C650 218 810 198 970 188 C1150 176 1400 198 1600 184 L1600 252 Z', '#7e9a76')
  // 洞口：墙 + 上半圆
  s += path('M686 252 L686 162 A114 114 0 0 1 914 162 L914 252 Z', '#39414a')
  // 洞内纵深：车道灯 + 逐渐收窄的路面
  for (let i = 0; i < 5; i++) {
    const t = i / 5
    s += rect(800 - 24 + i * 11, 150 - i * 12, 48 - i * 10, 5, 'rgba(255,233,168,' + (0.55 - t * 0.4).toFixed(2) + ')', 2)
  }
  s += path('M742 252 L858 252 L826 176 L774 176 Z', '#4a525b')
  s += path('M800 252 L800 178', 'none', ' stroke="#d8cfa0" stroke-width="3" stroke-dasharray="10 12"')
  // 洞门加固环
  s += path('M686 252 L686 162 A114 114 0 0 1 914 162 L914 252', 'none', ' stroke="#cbd1d3" stroke-width="11"')
  s += path('M686 252 L686 162 A114 114 0 0 1 914 162 L914 252', 'none', ' stroke="#9aa3a8" stroke-width="3"')
  // 隧道名牌
  s += rect(760, 8, 80, 26, '#2f6f8f', 4)
  s += rect(772, 16, 56, 10, 'rgba(255,255,255,.8)', 2)
  // 洞口两侧的反光柱与树
  for (let i = 0; i < 5; i++) s += rect(120 + i * 96, 214, 8, 38, '#e8e2c0', 2)
  s += pineTree(80, 252, 1.1) + pineTree(300, 252, 0.9) + pineTree(1420, 252, 1.0) + pineTree(1560, 252, 0.85)
  s += lamp(560, 252, 128, '#6a7176', '#ffe9a8')
  return s
}

// 大桥：水面 + 悬索桥塔 + 主缆吊索
// ⚠ 桥塔/主缆必须用比天空暗一档的灰：早先用了 #c2cbd1，在浅蓝天空和远景城市天际线前面
//   几乎完全看不见，整格只剩一条水带，看不出是桥。
function midBridge() {
  let s = ''
  // 水面（带远近明暗：远处亮、近处暗）
  s += rect(0, 196, 1600, 56, '#7ba7bb')
  s += rect(0, 196, 1600, 18, 'rgba(255,255,255,.16)')
  s += rect(0, 236, 1600, 16, 'rgba(20,50,66,.14)')
  for (let i = 0; i < 22; i++) s += rect(10 + i * 72, 206 + (i % 3) * 14, 46, 3, 'rgba(255,255,255,.45)', 1)
  // 桥面边缘（远侧护栏）
  s += rect(0, 186, 1600, 18, '#a3aeb5')
  s += rect(0, 186, 1600, 4, '#dfe6ea')
  s += rect(0, 200, 1600, 4, 'rgba(30,45,55,.18)')

  // 主缆曲线：塔顶 (360,40) / (1230,40) 最高，中跨垂到最低 y≈152，两端锚在岸上
  // ⚠ 别写反成「中间高两侧低」——那只画得出拱桥，主缆必须是垂的。
  const cableY = x => {
    if (x < 360) return 40 + (360 - x) / 400 * 56
    if (x > 1230) return 40 + (x - 1230) / 410 * 56
    const k = (x - 795) / 435
    return 40 + 112 * (1 - k * k)
  }
  // 吊索（先画，压在缆下面）
  for (let i = 0; i <= 20; i++) {
    const x = 20 + i * 78
    const yTop = cableY(x)
    if (yTop < 184) s += rect(x, yTop, 3, 186 - yTop, 'rgba(120,132,140,.85)')
  }
  // 主缆：后面一条压暗、前面一条提亮，桥才有厚度
  const cablePath = (dy, color, w) => {
    let d = `M-40 ${96 + dy}`
    d += ` L360 ${40 + dy}`
    for (let x = 380; x <= 1210; x += 30) d += ` L${x} ${cableY(x) + dy}`
    d += ` L1230 ${40 + dy} L1640 ${96 + dy}`
    return path(d, 'none', ` stroke="${color}" stroke-width="${w}" stroke-linejoin="round"`)
  }
  s += cablePath(10, '#7c878e', 7)
  s += cablePath(0, '#5f6a72', 8)

  // 桥塔（H 形，深灰才有体积感）
  const tower = x => {
    let g = rect(x - 11, 34, 22, 218, '#7e8b95')
    g += rect(x - 11, 34, 7, 218, 'rgba(255,255,255,.22)')
    g += rect(x - 11, 34, 22, 218, 'none', 0, ' stroke="rgba(40,56,66,.35)" stroke-width="2"')
    g += rect(x - 36, 26, 72, 14, '#8f9ca5', 3)
    g += rect(x - 36, 26, 72, 4, 'rgba(255,255,255,.3)', 2)
    g += rect(x - 32, 92, 64, 11, '#8b98a1', 2)
    g += rect(x - 32, 150, 64, 11, '#8b98a1', 2)
    return g
  }
  s += tower(360) + tower(1230)
  // 塔基没入水面
  s += rect(360 - 30, 190, 60, 12, 'rgba(20,50,66,.22)')
  s += rect(1230 - 30, 190, 60, 12, 'rgba(20,50,66,.22)')
  return s
}

// 桥头堡：石塔 + 垛口 + 引桥
// ⚠ 石头色必须比远山暗：第一版用了 #b0b4ae，在雾化的山体前面基本等于隐形。
function midFort() {
  let s = ''
  // 引桥：桥面 + 拱洞（拱洞比桥面深，才不像贴纸）
  s += rect(0, 202, 1000, 14, '#9aa3a8')
  s += rect(0, 202, 1000, 4, '#d3dade')
  s += rect(0, 216, 1000, 36, '#8b949a')
  for (let i = 0; i < 5; i++) {
    s += path(`M${56 + i * 190} 252 L${56 + i * 190} 222 A56 56 0 0 1 ${168 + i * 190} 222 L${168 + i * 190} 252 Z`, '#5f686d')
  }
  for (let i = 0; i < 6; i++) s += rect(30 + i * 190, 248, 190, 5, 'rgba(40,52,58,.18)')
  // 堡体
  s += rect(1030, 92, 310, 160, '#8e968f')
  s += rect(1030, 92, 310, 12, '#a4aba3')
  s += rect(1030, 92, 10, 160, 'rgba(255,255,255,.16)')
  for (let i = 0; i < 6; i++) s += rect(1042 + i * 49, 64, 32, 32, '#828a84')
  // 窗与门
  s += rect(1082, 132, 34, 44, '#4a504c', 4) + rect(1262, 132, 34, 44, '#4a504c', 4)
  s += path('M1150 252 L1150 188 A40 40 0 0 1 1230 188 L1230 252 Z', '#4a504c')
  s += path('M1150 252 L1150 188 A40 40 0 0 1 1230 188', 'none', ' stroke="#6d746e" stroke-width="5"')
  // 石缝
  for (let i = 0; i < 6; i++) s += rect(1030, 124 + i * 22, 310, 2, 'rgba(0,0,0,.10)')
  // 旗杆与旗
  s += rect(1186, 20, 5, 46, '#767d78')
  s += path('M1191 24 L1258 38 L1191 54 Z', '#a2553f')
  // 塔前路障与树
  s += rect(996, 216, 14, 36, '#e0a13a') + rect(996, 230, 14, 8, '#3a3f44')
  s += roundTree(700, 252, 0.8) + roundTree(900, 252, 0.65)
  s += lamp(960, 252, 126, '#6a7176')
  return s
}

// 立交：上下两层高架 + 环形匝道
// ⚠ 环道如果只画一条 ellipse 描边，看着就是个悬空的椭圆环；必须叠「暗底 + 亮面」
//   再补桥墩，才有管状高架的厚度。
function midInterchange() {
  let s = ''
  // 下层主路高架（横向）：顶面亮、侧面暗，靠这两条边拉开厚度
  s += rect(0, 206, 1600, 36, '#7f8a91')
  s += path('M-20 206 L1600 206 L1600 218 L-20 218 Z', '#96a1a8')
  s += rect(0, 206, 1600, 4, '#cfd7db')
  for (let i = 0; i < 8; i++) s += rect(70 + i * 210, 242, 24, 10, 'rgba(40,52,60,.25)')
  // 环状匝道：暗底椭圆 + 上移一点的亮面 = 管状（只画一条描边是个悬空圆环）
  s += '<ellipse cx="980" cy="164" rx="272" ry="86" fill="none" stroke="#6f7a81" stroke-width="30"/>'
  s += '<ellipse cx="980" cy="158" rx="272" ry="82" fill="none" stroke="#9aa5ac" stroke-width="22"/>'
  s += '<ellipse cx="980" cy="154" rx="272" ry="80" fill="none" stroke="#cdd5d9" stroke-width="4"/>'
  // 环道的桥墩：沿下半个圆落地
  for (let i = 0; i < 9; i++) {
    const a = Math.PI * (0.1 + i * 0.1)
    const px = 980 - 272 * Math.cos(a)
    const py = 158 + 82 * Math.sin(a)
    if (py > 170) s += rect(px - 7, py, 14, 252 - py, '#71797f')
  }
  // 顶层横跨：画在高处（y 40~68），桥墩只落在环道左右外侧，避免穿帮
  ;[140, 330, 1560].forEach(px => {
    s += rect(px, 60, 18, 192, '#6f787e')
    s += rect(px, 60, 6, 192, 'rgba(255,255,255,.14)')
  })
  s += path('M-20 40 L1600 40 L1600 66 L-20 66 Z', '#7a858c')
  s += path('M-20 40 L1600 46 L1600 54 L-20 48 Z', '#9aa5ac')
  s += rect(0, 38, 1600, 4, '#dde4e8')
  // 匝道与地面之间的投影
  s += '<ellipse cx="980" cy="244" rx="272" ry="10" fill="rgba(30,42,50,.10)"/>'
  // 路牌与灯
  s += rect(150, 96, 12, 110, '#8b959b') + rect(96, 92, 320, 12, '#9aa5ab', 3)
  s += rect(118, 112, 180, 48, '#2f7d4f', 4)
  s += rect(136, 126, 106, 11, 'rgba(255,255,255,.85)', 2)
  s += rect(136, 143, 68, 9, 'rgba(255,255,255,.55)', 2)
  s += roundTree(470, 252, 0.8) + roundTree(600, 252, 0.66) + roundTree(1420, 252, 0.7)
  s += lamp(440, 252, 130) + lamp(700, 252, 128)
  return s
}

function midNeon() {
  let s = ''
  // 塔体：平顶 + 檐口 + 竖向光带（尖顶会超出图层被裁成平头）
  const tower = (x, w, h, c1, c2) => {
    const top = 252 - h
    return path(`M${x} ${top} L${x + w} ${top} L${x + w} 252 L${x} 252 Z`, c1)
      + rect(x, top - 8, w, 8, c2)
      + rect(x + w / 2 - 2, top, 4, h, 'rgba(123,215,232,.22)')
  }
  s += tower(60, 122, 170, '#2f2748', '#463667')
  s += winGrid(78, 106, 3, 4, 22, 14, 14, 22, 'rgba(123,215,232,.62)')
  s += tower(290, 96, 215, '#2a2342', '#3d2f5c')
  s += winGrid(306, 62, 2, 4, 22, 14, 12, 22, 'rgba(226,124,214,.58)')
  s += tower(700, 112, 158, '#2f2748', '#463667')
  s += winGrid(718, 118, 3, 3, 22, 14, 8, 22, 'rgba(255,206,120,.55)')
  s += tower(990, 132, 230, '#282040', '#3a2c58')
  s += rect(1054, 4, 3, 24, '#7bd7e8', '', ' opacity=".85"')
  s += winGrid(1008, 44, 3, 5, 22, 14, 10, 22, 'rgba(123,215,232,.6)')
  s += tower(1300, 112, 186, '#2f2748', '#463667')
  s += winGrid(1318, 92, 3, 4, 22, 14, 8, 22, 'rgba(226,124,214,.5)')
  // 空中连廊：连接两侧塔体，带发光边
  s += rect(180, 146, 522, 14, '#372c53') + rect(180, 146, 522, 3, 'rgba(123,215,232,.8)')
  s += rect(180, 206, 522, 14, '#33294b') + rect(180, 206, 522, 3, 'rgba(123,215,232,.45)')
  // 全息灯牌（叠一层半透明光晕）
  const holo = (x, y, w, h, c, c2) => rect(x - 12, y - 12, w + 24, h + 24, c, 8, ' opacity="0.3"')
    + rect(x, y, w, h, c, 3, ' opacity="0.65"')
    + rect(x, y, w, h, 'none', 3, ` stroke="${c2}" stroke-width="2" opacity=".9"`)
  s += holo(556, 54, 124, 48, 'rgba(123,215,232,.3)', '#7bd7e8')
  s += rect(570, 64, 78, 8, 'rgba(224,250,255,.85)', 2) + rect(570, 80, 48, 8, 'rgba(224,250,255,.55)', 2)
  s += holo(910, 96, 96, 40, 'rgba(226,124,214,.3)', '#e27cd6')
  s += holo(1230, 36, 110, 46, 'rgba(255,206,120,.26)', '#ffce78')
  // 水晶柱（幻之都西门的标志物）
  const crystal = (x, base) => rect(x - 9, base - 96, 18, 96, 'rgba(140,220,240,.4)', 5)
    + rect(x - 4, base - 88, 8, 80, 'rgba(210,248,255,.8)', 3)
    + circ(x, base - 96, 12, 'rgba(180,240,255,.45)')
  s += crystal(1466, 252) + crystal(1530, 252)
  s += lamp(660, 252, 132, '#3a3450', '#7bd7e8') + lamp(1180, 252, 132, '#3a3450', '#7bd7e8')
  return s
}

/* ---------- 各主题近景（地面/路面，滚动最快） ---------- */
function baseGround(t, H = 300) {
  let s = rect(0, 0, 1600, H, t.shoulder)
  s += rect(0, 18, 1600, H - 18, t.road)
  s += rect(0, 18, 1600, 8, 'rgba(255,255,255,.22)')
  return s
}
function dashes(color, y, w = 90, gap = 110, h = 8) {
  let s = ''
  for (let x = -20; x < 1600; x += w + gap) s += rect(x, y, w, h, color, 2)
  return s
}
function nearPaved(t) {
  let s = baseGround(t)
  s += dashes(t.line, 150, 96, 120, 9)
  s += rect(0, 262, 1600, 38, 'rgba(0,0,0,.14)')
  for (let i = 0; i < 12; i++) s += rect(30 + i * 136, 60, 54, 5, 'rgba(255,255,255,.10)')
  return s
}
function nearGrass(t) {
  let s = rect(0, 0, 1600, 300, t.ground)
  s += rect(0, 34, 1600, 10, 'rgba(255,255,255,.18)')
  s += rect(0, 44, 1600, 256, t.road)
  s += dashes(t.line, 158, 100, 118, 9)
  for (let i = 0; i < 34; i++) {
    const x = 10 + i * 47
    s += path(`M${x} 40 q6 -16 12 0 Z`, '#5f9a4a', ' opacity="0.85"')
  }
  for (let i = 0; i < 16; i++) s += rect(60 + i * 100, 70, 46, 5, 'rgba(255,255,255,.10)')
  return s
}
function nearField(t) {
  let s = rect(0, 0, 1600, 300, t.ground)
  for (let i = 0; i < 40; i++) s += rect(i * 42, 0, 18, 36, 'rgba(255,255,255,.13)')
  s += rect(0, 40, 1600, 10, 'rgba(255,255,255,.16)')
  s += rect(0, 50, 1600, 250, t.road)
  s += rect(0, 50, 1600, 6, 'rgba(255,255,255,.14)')
  s += dashes(t.line, 160, 80, 130, 8)
  for (let i = 0; i < 22; i++) {
    const x = 20 + i * 74
    s += path(`M${x} 44 q8 -20 16 0 Z`, '#4f8f3f')
  }
  return s
}
// 桥面/高架：没有草地，两侧是护栏
function nearRail(t) {
  let s = baseGround(t)
  s += dashes(t.line, 152, 96, 118, 9)
  // 护栏立柱 + 横梁（近景里当作远侧栏杆）
  for (let i = 0; i < 26; i++) s += rect(20 + i * 62, 22, 7, 30, 'rgba(255,255,255,.55)')
  s += rect(0, 20, 1600, 6, 'rgba(255,255,255,.7)')
  s += rect(0, 262, 1600, 38, 'rgba(0,0,0,.14)')
  return s
}
function nearNeon(t) {
  let s = rect(0, 0, 1600, 300, t.ground)
  s += rect(0, 40, 1600, 260, t.road)
  s += dashes(t.line, 150, 110, 130, 7)
  s += rect(0, 40, 1600, 4, 'rgba(123,215,232,.55)')
  s += rect(0, 44, 1600, 22, 'rgba(123,215,232,.07)')
  for (let i = 0; i < 10; i++) s += rect(40 + i * 160, 96, 60, 4, 'rgba(226,124,214,.35)')
  return s
}

/**
 * 主题表。band = 地平线以下的远处地面色，用来把 far 层底部补满（漏了就是一条黑缝）。
 */
const THEMES = {
  plaza: {
    tag: '广场 · 石雕喷泉',
    sky: ['#79bde8', '#b6dcf3', '#f4eedb'],
    sun: { x: 1180, y: 120, r: 46, c: '#fff6d0' },
    hills: [['#c6dbc9', 0.66], ['#aac9b6', 0.76]],
    skyline: { color: '#b8cad6', base: 0.66, kind: 'dome' },
    band: '#c9c3ae', ground: '#dcd4bf', road: '#b3ac9c', shoulder: '#c8c0aa', line: '#f0e4b8',
    mid: midPlaza, near: nearPaved
  },
  avenue: {
    tag: '王都大道 · 高楼与行道树',
    sky: ['#69b1e2', '#a9d6f2', '#eaf5fc'],
    sun: { x: 300, y: 130, r: 40, c: '#ffffff' },
    hills: [['#c2d6de', 0.62], ['#a8c1cc', 0.72]],
    skyline: { color: '#c3d1da', base: 0.62, kind: 'city' },
    band: '#b7c8b3', ground: '#c3dd9e', road: '#6f7883', shoulder: '#b7c9a2', line: '#e8e2b6',
    mid: midAvenue, near: nearGrass
  },
  residence: {
    tag: '居住区 · 坡屋顶小巷',
    sky: ['#f6b97c', '#ffd9a8', '#fff3dc'],
    sun: { x: 1180, y: 150, r: 52, c: '#fff0c4' },
    hills: [['#e0c9a8', 0.64], ['#c9b18f', 0.74]],
    skyline: { color: '#d9c3a6', base: 0.64, kind: 'town' },
    band: '#cbb894', ground: '#d3c19c', road: '#7a7266', shoulder: '#c2b193', line: '#efe3bf',
    mid: midResidence, near: nearPaved
  },
  farm: {
    tag: '郊野 · 田野与风车',
    sky: ['#8fcdee', '#c6e8f7', '#f0fbe9'],
    sun: { x: 1320, y: 110, r: 42, c: '#fffbe0' },
    hills: [['#bcdcc0', 0.58], ['#9cc9a8', 0.68]],
    skyline: { color: '#c4dcc6', base: 0.58, kind: 'none' },
    band: '#a9cf99', ground: '#a7d474', road: '#8d8069', shoulder: '#93c465', line: '#d8cfa0',
    mid: midFarm, near: nearField
  },
  gate: {
    tag: '关隘 · 石墙与塔楼',
    sky: ['#a9c3d2', '#cfdfe6', '#f2f6f4'],
    sun: { x: 420, y: 145, r: 34, c: '#fdfbf0' },
    hills: [['#b8c9c0', 0.62], ['#9fb3a9', 0.72]],
    skyline: { color: '#c0cdc9', base: 0.62, kind: 'none' },
    band: '#b5bcb2', ground: '#c9cfc7', road: '#6b7176', shoulder: '#b3bab2', line: '#e5e8de',
    mid: midGate, near: nearPaved
  },
  forest: {
    tag: '林道 · 白杨与松林',
    sky: ['#7fc0e6', '#b8e0f2', '#e6f6ea'],
    sun: { x: 1080, y: 108, r: 36, c: '#f6ffe8' },
    hills: [['#a6cbb0', 0.56], ['#7fb292', 0.66]],
    skyline: { color: '#9dc4a8', base: 0.56, kind: 'forest' },
    band: '#8db992', ground: '#7fb46a', road: '#5f6559', shoulder: '#6fa25c', line: '#dfd8a8',
    mid: midForest, near: nearGrass
  },
  highway: {
    tag: '高速 · 龙门架与匝道',
    sky: ['#6fb2e0', '#a8d3ee', '#e8f3fa'],
    sun: { x: 1080, y: 118, r: 38, c: '#ffffff' },
    hills: [['#c2d8c6', 0.6], ['#a5c3ab', 0.7]],
    skyline: { color: '#c3d3d6', base: 0.6, kind: 'none' },
    band: '#bccdba', ground: '#b9d29a', road: '#6b7278', shoulder: '#adc196', line: '#eae4b4',
    mid: midHighway, near: nearGrass
  },
  station: {
    tag: '车站 · 站房与月台',
    sky: ['#7fb8dd', '#b3d9ee', '#e9f2f7'],
    sun: { x: 340, y: 118, r: 38, c: '#fffdf0' },
    hills: [['#c6d2cf', 0.62], ['#adbdb8', 0.72]],
    skyline: { color: '#c2cdd0', base: 0.62, kind: 'town' },
    band: '#bcc4be', ground: '#c8d2c4', road: '#71767c', shoulder: '#b6bfb6', line: '#e8e6c8',
    mid: midStation, near: nearPaved
  },
  toll: {
    tag: '收费站 · 收费亭与抬杆',
    sky: ['#6fb2e0', '#a8d3ee', '#e8f3fa'],
    sun: { x: 1180, y: 112, r: 36, c: '#ffffff' },
    hills: [['#c2d8c6', 0.6], ['#a5c3ab', 0.7]],
    skyline: { color: '#c3d3d6', base: 0.6, kind: 'none' },
    band: '#bccdba', ground: '#b9d29a', road: '#6b7278', shoulder: '#adc196', line: '#eae4b4',
    mid: midToll, near: nearPaved
  },
  exit: {
    tag: '出口 · 分流匝道',
    sky: ['#6cb0df', '#a6d2ee', '#e7f2fa'],
    sun: { x: 980, y: 116, r: 36, c: '#ffffff' },
    hills: [['#c0d7c4', 0.6], ['#a3c2a9', 0.7]],
    skyline: { color: '#c2d2d5', base: 0.6, kind: 'none' },
    band: '#bccdba', ground: '#b9d29a', road: '#6b7278', shoulder: '#adc196', line: '#eae4b4',
    mid: midExit, near: nearGrass
  },
  viewpoint: {
    tag: '观景台 · 山口与平台',
    sky: ['#8cc6e8', '#c2e2f2', '#f1f8ea'],
    sun: { x: 1260, y: 100, r: 44, c: '#fffce4' },
    hills: [['#a8c9b6', 0.56], ['#86b09c', 0.66]],
    skyline: { color: '#9dc0aa', base: 0.56, kind: 'forest' },
    band: '#9dbe9a', ground: '#a8cc8e', road: '#6f767c', shoulder: '#9dbd8c', line: '#eae4b4',
    mid: midViewpoint, near: nearRail
  },
  tunnel: {
    tag: '隧道 · 洞口与洞内灯火',
    sky: ['#8fa3b0', '#b6c6ce', '#dde6e8'],
    sun: { x: 420, y: 96, r: 30, c: '#dfe9ec' },
    hills: [['#93a89a', 0.6], ['#7d9484', 0.7]],
    skyline: { color: '#9fb0a6', base: 0.6, kind: 'forest' },
    band: '#8b9d8d', ground: '#93ab8a', road: '#5e646a', shoulder: '#84967f', line: '#ded8a6',
    mid: midTunnel, near: nearPaved
  },
  bridge: {
    tag: '大桥 · 悬索与水面',
    sky: ['#6cb4e4', '#a6d6f2', '#e8f5fb'],
    sun: { x: 300, y: 108, r: 40, c: '#ffffff' },
    hills: [['#b6cbd2', 0.6], ['#9ab4bd', 0.7]],
    skyline: { color: '#b9c9cf', base: 0.6, kind: 'city' },
    band: '#8db8c8', ground: '#8db8c8', road: '#6a7076', shoulder: '#a9b4ba', line: '#eae4b4',
    mid: midBridge, near: nearRail
  },
  fort: {
    tag: '桥头堡 · 石塔与引桥',
    sky: ['#a8b6c6', '#cdd8e2', '#eef3f6'],
    sun: { x: 1160, y: 128, r: 34, c: '#fdf8e8' },
    hills: [['#b4bdb8', 0.6], ['#9ca6a2', 0.7]],
    skyline: { color: '#bcc5c0', base: 0.6, kind: 'none' },
    band: '#b2b8b2', ground: '#c0c6bd', road: '#6b7176', shoulder: '#b0b7ae', line: '#e6e2c6',
    mid: midFort, near: nearPaved
  },
  interchange: {
    tag: '立交 · 多层高架',
    sky: ['#74b6e2', '#acd6ee', '#eaf4fa'],
    sun: { x: 640, y: 104, r: 38, c: '#ffffff' },
    hills: [['#c2d6c8', 0.6], ['#a6c2ac', 0.7]],
    skyline: { color: '#c6d3d6', base: 0.6, kind: 'none' },
    band: '#bccdba', ground: '#b9d29a', road: '#6b7278', shoulder: '#adc196', line: '#eae4b4',
    mid: midInterchange, near: nearRail
  },
  service: {
    tag: '服务区 · 加油与便利店',
    sky: ['#5fa9dd', '#9dcbe8', '#dfeef7'],
    sun: { x: 320, y: 118, r: 34, c: '#ffffff' },
    hills: [['#bcd3c4', 0.6], ['#9fbcaa', 0.7]],
    skyline: { color: '#bdcdd2', base: 0.6, kind: 'none' },
    band: '#afc1a5', ground: '#b9c8a8', road: '#62686f', shoulder: '#a7b79a', line: '#eae4b4',
    mid: midService, near: nearGrass
  },
  neon: {
    tag: '六芒星城 · 全息灯牌',
    sky: ['#241a44', '#4a2d6e', '#8a4a7a'],
    moon: { x: 1150, y: 110, r: 38, c: '#ffe9c0' }, stars: true,
    hills: [['#3b2a55', 0.62], ['#2b1f42', 0.72]],
    skyline: { color: '#41305e', base: 0.62, kind: 'city' },
    band: '#332c4a', ground: '#3b3a52', road: '#2f3340', shoulder: '#43445c', line: '#7bd7e8',
    mid: midNeon, near: nearNeon
  }
}

/* ---------- 远景（天空 + 日/月 + 云 + 山 + 天际线） ---------- */
function buildFar(t, H = 600) {
  const K = 0.865
  let s = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 ${H}" preserveAspectRatio="none">`
  s += '<defs><linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">'
  t.sky.forEach((c, i) => {
    s += `<stop offset="${(i / (t.sky.length - 1) * 100).toFixed(0)}%" stop-color="${c}"/>`
  })
  s += '</linearGradient></defs>'
  s += rect(0, 0, 1600, H, 'url(#sky)')
  s += `<g transform="scale(1,${K})">`

  if (t.stars) {
    const pts = [[80, 60], [210, 120], [330, 48], [470, 96], [600, 40], [720, 130], [860, 70], [980, 30],
      [1090, 105], [1230, 62], [1340, 118], [1470, 52], [1560, 96], [400, 160], [900, 170]]
    pts.forEach(([x, y], i) => { s += circ(x, y, i % 3 ? 1.4 : 2.2, '#ffffff', ` opacity="${i % 2 ? .55 : .85}"`) })
  }
  // 日 / 月（径向渐变柔化，纯色圆会有硬边）
  if (t.sun) {
    s += `<defs><radialGradient id="sunG"><stop offset="0%" stop-color="${t.sun.c}" stop-opacity="0.95"/><stop offset="55%" stop-color="${t.sun.c}" stop-opacity="0.55"/><stop offset="100%" stop-color="${t.sun.c}" stop-opacity="0"/></radialGradient></defs>`
    s += circ(t.sun.x, t.sun.y, t.sun.r * 3.4, 'url(#sunG)')
    s += circ(t.sun.x, t.sun.y, t.sun.r, t.sun.c)
  }
  if (t.moon) {
    s += `<defs><radialGradient id="moonG"><stop offset="0%" stop-color="${t.moon.c}" stop-opacity="0.6"/><stop offset="100%" stop-color="${t.moon.c}" stop-opacity="0"/></radialGradient></defs>`
    s += circ(t.moon.x, t.moon.y, t.moon.r * 3.2, 'url(#moonG)')
    s += circ(t.moon.x, t.moon.y, t.moon.r, t.moon.c)
    s += circ(t.moon.x - 9, t.moon.y - 7, t.moon.r * 0.82, 'rgba(255,255,255,.10)')
  }
  // 云
  const cloud = (x, y, s2, op) => `<g opacity="${op}">`
    + circ(x, y, 22 * s2, '#ffffff') + circ(x + 26 * s2, y + 6 * s2, 16 * s2, '#ffffff')
    + circ(x - 26 * s2, y + 8 * s2, 14 * s2, '#ffffff') + rect(x - 30 * s2, y + 8 * s2, 60 * s2, 14 * s2, '#ffffff', 7 * s2)
    + '</g>'
  s += cloud(240, 92, 1.05, .82) + cloud(700, 62, .8, .7) + cloud(1250, 110, .95, .75) + cloud(1520, 70, .7, .6)

  // 远山：首尾同高，横向平铺才不会出现接缝
  t.hills.forEach(([color, ratio], idx) => {
    const baseY = H * ratio
    const amp = idx === 0 ? 78 : 54
    const off = idx === 0 ? 0 : 40
    const d = [
      [0, baseY], [130, baseY - amp * .7], [280, baseY - amp * .2], [430, baseY - amp],
      [600, baseY - amp * .45], [740, baseY - amp * .9], [900, baseY - amp * .25],
      [1060, baseY - amp * .8], [1220, baseY - amp * .3], [1400, baseY - amp * .75], [1600, baseY]
    ].map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${(y + off).toFixed(1)}`).join(' ')
    s += path(`${d} L1600 ${H} L0 ${H} Z`, color)
  })

  // 天际线
  const sk = t.skyline
  if (sk && sk.kind !== 'none') {
    const baseY = H * sk.base + 20
    const c = sk.color
    if (sk.kind === 'city') {
      const bs = [[30, 120, 70], [110, 190, 54], [176, 90, 84], [268, 150, 62], [344, 210, 58], [412, 130, 74],
        [500, 175, 60], [572, 100, 88], [672, 215, 56], [740, 140, 76], [828, 165, 64], [906, 105, 82],
        [1000, 200, 58], [1070, 135, 72], [1154, 180, 60], [1226, 95, 86], [1324, 205, 54], [1390, 145, 70],
        [1472, 175, 62], [1546, 110, 66]]
      bs.forEach(([x, h, w]) => { s += rect(x, baseY - h, w, h, c) })
    } else if (sk.kind === 'dome') {
      s += rect(40, baseY - 130, 150, 130, c)
      s += path(`M40 ${baseY - 130} q75 -80 150 0 Z`, c)
      s += rect(230, baseY - 190, 90, 190, c) + path(`M275 ${baseY - 232} l45 42 l-90 0 Z`, c)
      s += rect(360, baseY - 120, 210, 120, c) + path(`M360 ${baseY - 120} q105 -70 210 0 Z`, c)
      s += rect(610, baseY - 160, 70, 160, c) + path(`M645 ${baseY - 200} l35 40 l-70 0 Z`, c)
      s += rect(720, baseY - 110, 240, 110, c) + path(`M720 ${baseY - 110} q120 -66 240 0 Z`, c)
      s += rect(1000, baseY - 175, 96, 175, c) + path(`M1048 ${baseY - 214} l48 39 l-96 0 Z`, c)
      s += rect(1136, baseY - 125, 200, 125, c) + path(`M1136 ${baseY - 125} q100 -74 200 0 Z`, c)
      s += rect(1376, baseY - 165, 84, 165, c)
      s += rect(1470, baseY - 115, 130, 115, c) + path(`M1470 ${baseY - 115} q65 -56 130 0 Z`, c)
    } else if (sk.kind === 'town') {
      for (let i = 0; i < 14; i++) {
        const x = 20 + i * 116, h = 70 + (i % 4) * 26, w = 96
        s += rect(x, baseY - h, w, h, c)
        s += path(`M${x - 10} ${baseY - h} L${x + w / 2} ${baseY - h - 34} L${x + w + 10} ${baseY - h} Z`, c)
      }
    } else if (sk.kind === 'forest') {
      for (let i = 0; i < 46; i++) {
        const x = 6 + i * 35, h = 60 + (i * 37 % 70)
        s += path(`M${x} ${baseY - h} L${x + 18} ${baseY} L${x - 18} ${baseY} Z`, c)
      }
    }
  }
  // 地平线空气雾：让远景退进去，避免山与天空硬接
  s += '<defs><linearGradient id="haze" x1="0" y1="0" x2="0" y2="1">'
    + '<stop offset="0%" stop-color="#ffffff" stop-opacity="0"/><stop offset="100%" stop-color="#ffffff" stop-opacity="0.4"/>'
    + '</linearGradient></defs>'
  s += rect(0, H * 0.56, 1600, H * 0.44, 'url(#haze)')
  s += '</g>'
  // 地平线以下的远处地面：与中景脚下接上，不留黑缝
  s += rect(0, H * K - 1, 1600, H * (1 - K) + 2, t.band || t.hills[1][0])
  s += '</svg>'
  return s
}

/** 近景叠加层：纵深渐变 + 地平线淡影 + 贴地飞掠的光带（跑起来才是速度感的主要来源） */
function depthOverlay() {
  let s = '<defs><linearGradient id="dp" x1="0" y1="0" x2="0" y2="1">'
    + '<stop offset="0%" stop-color="#000000" stop-opacity="0"/><stop offset="100%" stop-color="#08111a" stop-opacity="0.26"/>'
    + '</linearGradient></defs>'
  s += rect(0, 30, 1600, 270, 'url(#dp)')
  s += rect(0, 0, 1600, 6, 'rgba(0,0,0,.10)')
  for (let i = 0; i < 8; i++) s += rect(i * 210 + 24, 262 + (i % 3) * 12, 148, 3, 'rgba(255,255,255,.11)', 2)
  return s
}

/* ---------- 大巴 ---------- */
/**
 * 疾驰的大巴（内联 SVG 片段，配合 coachPassage.vue 的 CSS 动画）。
 * 渐变 id 加 cp 前缀：内联进 DOM 后 id 是全局的，直接用 body / glass 会撞上页面里其他元素。
 */
export function buildBusSvg() {
  const gold = '#d8b45c'
  let s = '<defs>'
  s += '<linearGradient id="cpBody" x1="0" y1="0" x2="0" y2="1">'
    + '<stop offset="0%" stop-color="#fbfdff"/><stop offset="46%" stop-color="#e8f1f7"/>'
    + '<stop offset="47%" stop-color="#dbe7f0"/><stop offset="100%" stop-color="#c3d4e1"/></linearGradient>'
  s += '<linearGradient id="cpGlass" x1="0" y1="0" x2="1" y2="1">'
    + '<stop offset="0%" stop-color="#5d8aa8"/><stop offset="55%" stop-color="#3a5f7d"/>'
    + '<stop offset="100%" stop-color="#28455e"/></linearGradient>'
  s += '<linearGradient id="cpStripe" x1="0" y1="0" x2="0" y2="1">'
    + '<stop offset="0%" stop-color="#3f7f9e"/><stop offset="100%" stop-color="#2a5f7a"/></linearGradient>'
  s += '<linearGradient id="cpBeam" x1="0" y1="0" x2="1" y2="0">'
    + '<stop offset="0%" stop-color="#ffe9a8" stop-opacity="0.62"/>'
    + '<stop offset="100%" stop-color="#ffe9a8" stop-opacity="0"/></linearGradient>'
  s += '</defs>'
  // 地面阴影
  s += '<ellipse cx="196" cy="169" rx="154" ry="7" fill="rgba(0,0,0,.22)"/>'
  // 车轮：先画轮、后画带轮拱缺口的车身，轮胎才会「装进」车里而不是被车身切掉
  const wheel = cx => '<g>'
    + circ(cx, 140, 28, '#1e242b') + circ(cx, 140, 21, '#2b333b')
    + `<g class="cp-wheel" style="transform-origin:${cx}px 140px">`
    + circ(cx, 140, 13, '#ccd5dd') + circ(cx, 140, 5, '#8b98a2')
    + rect(cx - 1.6, 121, 3.2, 38, '#b1bcc4') + rect(cx - 19, 138.4, 38, 3.2, '#b1bcc4')
    + '</g></g>'
  s += wheel(120) + wheel(280)
  // 车身：底边挖出两个轮拱
  s += path('M60 34 L300 34 Q330 34 330 62 L330 132 L312 132 A32 32 0 0 0 248 132 L152 132 A32 32 0 0 0 88 132 L76 132 Q60 132 60 114 Z', 'url(#cpBody)')
  // 车顶天线
  s += rect(231, 16, 3, 18, '#9fb0bd', 2) + circ(232.5, 15, 3.2, '#c3ced6')
  // 深色玻璃带 + 窗格
  s += path('M72 46 L318 46 L318 84 L72 84 Z', 'url(#cpGlass)', ' opacity="0.96"')
  for (let i = 0; i < 6; i++) s += rect(78 + i * 40, 50, 32, 30, 'rgba(180,215,235,.55)', 3)
  s += path('M300 46 L318 46 L318 86 L300 86 Z', '#7fb0cb', ' opacity="0.75"')
  // 腰线
  s += rect(60, 92, 254, 12, 'url(#cpStripe)') + rect(60, 106, 254, 3, gold)
  // 车门缝
  s += rect(268, 46, 3, 86, 'rgba(255,255,255,.35)')
  // 前灯（车头朝右）+ 车尾示廓灯
  s += rect(322, 98, 10, 16, '#ffe9a8', 4) + rect(62, 98, 9, 12, '#e0785f', 3)
  // 后视镜
  s += rect(332, 58, 8, 4, '#8fa3b0') + rect(340, 52, 5, 16, '#8fa3b0', 2)
  // 车头灯束
  s += '<g class="cp-beam">' + path('M332 98 L382 80 L382 124 L332 112 Z', 'url(#cpBeam)') + '</g>'
  // 侧身高光 + 车窗斜反光
  s += rect(64, 36, 118, 4, 'rgba(255,255,255,.75)', 2)
  s += path('M96 46 L128 46 L98 84 L66 84 Z', 'rgba(255,255,255,.10)')
  // 速度线
  s += '<g stroke="rgba(255,255,255,.6)" stroke-width="2.6" stroke-linecap="round">'
  ;[[14, 96, 26], [22, 112, 18], [8, 126, 30]].forEach(([dx, y, len], i) => {
    s += `<line class="cp-speed" style="animation-delay:${i * 0.16}s" x1="${dx}" y1="${y}" x2="${len + dx}" y2="${y}"/>`
  })
  s += '</g>'
  return s
}

/* ---------- 描述 → 场景主题 ---------- */
// 只放「强特征」词：单字（林 / 山 / 门 / 路）一律不收，否则「花木修剪」会被判成森林、
// 「大门处」会把娱乐城判成城门。avenue 不进这张表，它是所有情况都不命中时的兜底。
// ⚠ 「幻之都」也不能当 neon 关键词 —— 官道沿线大半节点的描述里都写着「王都与幻之都的分界」。
const HIGH_RULES = [
  ['service', ['服务区', '停车区', '加油', '便利店']],
  ['gate', ['检查站', '临检', '界域', '城门', '南大门', '北大门', '城墙', '关卡', '哨卡']],
  // ⚠ 「六芒星」不能当 neon 关键词：王都车站的描述里写的是「上车可直达幻之都六芒星城」，
  //   那是目的地不是当前场景，会把出发站画成赛博夜景。只有水晶柱/全息/灯牌/电玩这些
  //   真的描述当地长相的词才算。
  ['neon', ['水晶柱', '全息', '灯牌', '电玩', '赛博', '霓虹灯', '连廊']],
  ['station', ['车站', '站台', '月台']],
  // 公路子类型必须排在 highway 之前，否则「东环收费站」「三岔立交」都会被归成大类 highway
  ['toll', ['收费站', '收费亭']],
  ['tunnel', ['隧道']],
  ['bridge', ['大桥', '跨海桥', '悬索桥']],
  ['fort', ['桥头堡']],
  ['interchange', ['立交']],
  ['viewpoint', ['观景台', '观景']],
  ['exit', ['出口', '匝道']],
  ['highway', ['高速', '高架', '公路']],
  ['forest', ['白杨', '松林', '森林', '树林', '林荫', '河湾', '郊野', '岔路', '山谷', '山口']],
  ['farm', ['农场', '田野', '麦田', '牧场', '村落', '菜地', '稻田']],
  ['residence', ['居住区', '住宅区', '住宅', '居民区', '民居', '宿舍', '小巷', '弄堂']],
  ['plaza', ['广场', '雕像', '喷泉', '纪念', '歌城', '博物馆', '美术馆', '剧院', '大教堂']]
]

function pickByRules(text) {
  if (!text) return null
  for (let i = 0; i < HIGH_RULES.length; i++) {
    const words = HIGH_RULES[i][1]
    for (let j = 0; j < words.length; j++) {
      if (text.indexOf(words[j]) >= 0) return HIGH_RULES[i][0]
    }
  }
  return null
}

/**
 * 按站点名与描述匹配场景主题：**名称优先**，名称给不出结论才看描述。
 * 名称优先的理由：「电玩居民区」描述里写的是「住宅区」，但玩家在那儿看到的就是赛博城；
 * 反过来「18号大街」名称里只有「大街」，得靠描述里的「全息灯牌」才知道是幻之都。
 * @param {string} name 站点名
 * @param {string} desc 站点描述（后端下发的 placedesc 摘要）
 */
export function matchSceneKey(name, desc) {
  return pickByRules(name) || pickByRules(desc) || 'avenue'
}

// 同一个主题只生成一次：24 站里通常反复出现同样几类场景，不缓存的话每站都要重算 3 张 SVG
const sceneCache = {}

/**
 * 取某主题的三层背景（已是可直接塞进 style 的 url(...)）。
 * @returns {{far: string, mid: string, near: string, tag: string}}
 */
export function getScene(sceneKey) {
  const key = THEMES[sceneKey] ? sceneKey : 'avenue'
  if (sceneCache[key]) return sceneCache[key]
  const t = THEMES[key]
  const scene = {
    far: toURI(buildFar(t)),
    mid: toURI(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 260" preserveAspectRatio="none">${t.mid()}</svg>`),
    near: toURI(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 300" preserveAspectRatio="none">${t.near(t)}${depthOverlay()}</svg>`),
    tag: t.tag
  }
  sceneCache[key] = scene
  return scene
}
